import React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, ArrowLeft } from 'lucide-react';
import { useData } from '@/contexts/DataContext';

const AffiliatedPage = () => {
  const { affiliates, loading } = useData();

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
          >
            <ArrowLeft size={16} />
            Back to Dashboard
          </Link>
          
          <div className="text-center">
            <h1 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-4">
              Affiliate Partners
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Discover our trusted partners and exclusive offers. These are carefully selected services that we recommend for your financial journey.
            </p>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="text-muted-foreground">Loading affiliate links...</div>
          </div>
        )}

        {/* No Affiliates State */}
        {!loading && (!affiliates || affiliates.length === 0) && (
          <div className="text-center py-12">
            <div className="max-w-md mx-auto">
              <ExternalLink size={48} className="text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2">No Affiliate Links Available</h3>
              <p className="text-muted-foreground">
                Affiliate links will appear here once they are added by the administrator.
              </p>
            </div>
          </div>
        )}

        {/* Affiliates Grid */}
        {!loading && affiliates && affiliates.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {affiliates.map((affiliate) => (
              <div key={affiliate.id} className="finance-card p-6 hover:shadow-lg transition-shadow">
                {/* Image Display */}
                {affiliate.imageUrl && (
                  <div className="mb-4">
                    <img 
                      src={affiliate.imageUrl} 
                      alt={affiliate.text}
                      className="w-full h-40 object-cover rounded-lg"
                      onError={(e) => {
                        console.log("Affiliate image failed to load:", affiliate.imageUrl);
                        e.currentTarget.src = "/placeholder.svg";
                      }}
                    />
                  </div>
                )}
                
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="font-display font-semibold text-foreground text-lg mb-2 line-clamp-2">
                      {affiliate.text}
                    </h3>
                    <span className="inline-block px-3 py-1 bg-purple-100 text-purple-800 text-sm rounded-full font-medium">
                      {affiliate.type}
                    </span>
                  </div>
                  <ExternalLink size={20} className="text-muted-foreground" />
                </div>

                {/* Description */}
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  Join this trusted platform and access exclusive features designed for your financial success. 
                  Special benefits available through our partnership.
                </p>

                {/* CTA Button */}
                <a
                  href={affiliate.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white text-center py-3 px-6 rounded-lg font-semibold hover:opacity-90 transition-opacity"
                >
                  Get Started Now →
                </a>
              </div>
            ))}
          </div>
        )}

        {/* Disclaimer */}
        {!loading && affiliates && affiliates.length > 0 && (
          <div className="mt-12 p-6 bg-muted/30 rounded-lg">
            <h3 className="font-semibold text-foreground mb-3">Affiliate Disclosure</h3>
            <p className="text-muted-foreground leading-relaxed">
              <strong>Disclaimer for affiliate marketing:</strong> This page contains affiliate links - I may earn a small commission if you purchase through them, at no extra cost to you.
              We only recommend products and services that we believe will provide value to our users. 
              Your support helps us maintain and improve this platform.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AffiliatedPage;
